import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Camera,
  Check,
  CloudOff,
  Crop as CropIcon,
  Focus,
  Loader2,
  RefreshCw,
  ScanText,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAccount } from '../../context/AccountContext';
import { GET_COLLECTION_FACETS } from '../../graphql';
import {
  BackgroundCalibration,
  ColorCalibration,
  FocusSupport,
  buildBackgroundProfile,
  buildCaptureMeta,
  captureFrame,
  detectFocusCapabilities,
  grabStill,
  listCameras,
  sampleNeutralGray,
  startStream,
  triggerAutofocus,
} from '../../lib/capture/camera';
import { QueuedItem, enqueue, listQueue, mintSku, removeItem } from '../../lib/capture/queue';
import { drainQueue, uploadItem } from '../../lib/capture/upload';
import { ensureOpenCv, ensureTesseract } from '../../lib/capture/vendor';
import { Loader } from '../common/PageLoader';

/**
 * Browser capture — "sign up and start shooting", no install.
 *
 * This replaces the local Node app that used to sit between a USB camera and
 * the API. Everything it did in the browser (camera control, calibration,
 * OpenCV auto-crop, Tesseract OCR) is here; everything it did on the server
 * (eBay, AI, the CMS proxy) already moved into the API server, so there is
 * nothing left to run locally.
 *
 * Two decisions shape the whole screen:
 *
 * 1. It writes with the ordinary session JWT, through `saveCollectionItem`,
 *    exactly like every other screen. The machine `smk_` tokens the studio app
 *    uses are tenant-scoped and long-lived, and a browser cannot hold one
 *    safely — any XSS would exfiltrate a credential that ingests into the whole
 *    account.
 *
 * 2. Nothing uploads from memory. A finished item goes to IndexedDB first and
 *    only then to the network, so a dropped connection costs a retry rather
 *    than the afternoon's work.
 */

type Side = 'front' | 'back' | 'other';
const SIDES: Side[] = ['front', 'back', 'other'];

interface Shot {
  blob: Blob;
  url: string;
  width: number;
  height: number;
  cropped?: boolean;
}

/** The card-template fields the capture screen collects. */
const FIELD_ORDER = [
  { key: 'player_name', label: 'Player' },
  { key: 'year', label: 'Year' },
  { key: 'card_number', label: 'Card #' },
  { key: 'variant', label: 'Variant' },
];

/** Taxonomy dimensions offered at capture time, in facet order. */
const TAXONOMY_ORDER = [
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'product_set', label: 'Set' },
  { key: 'team', label: 'Team' },
  { key: 'sport', label: 'Sport' },
  { key: 'league', label: 'League' },
];

export function CaptureLayout() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const client = useApolloClient();
  const navigate = useNavigate();

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const focusRef = useRef<FocusSupport>({ track: null, singleShot: false, continuous: false });

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [resolution, setResolution] = useState<string>('—');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [afBeforeCapture, setAfBeforeCapture] = useState(true);
  const [focusing, setFocusing] = useState(false);

  const [bgCal, setBgCal] = useState<BackgroundCalibration | null>(null);
  const [colorCal, setColorCal] = useState<ColorCalibration | null>(null);
  const [colorPicker, setColorPicker] = useState<HTMLCanvasElement | null>(null);

  const [shots, setShots] = useState<Partial<Record<Side, Shot>>>({});
  const [fields, setFields] = useState<Record<string, string>>({});
  const [taxonomies, setTaxonomies] = useState<Record<string, string>>({});
  const [ocrText, setOcrText] = useState<string>('');
  const [ocrSuggestions, setOcrSuggestions] = useState<{ names: string[]; numbers: string[] }>({
    names: [],
    numbers: [],
  });
  const [busy, setBusy] = useState<string | null>(null);

  const [queue, setQueue] = useState<QueuedItem[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const { data: facetData } = useQuery(GET_COLLECTION_FACETS, {
    variables: { accountId },
    skip: !accountId,
  });

  // Existing terms, offered as datalist suggestions. Closed dimensions reject
  // unknown terms server-side, so suggesting what already exists is the
  // difference between a save that works and one that comes back rejected.
  const facetTerms: Record<string, string[]> = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const f of facetData?.collectionFacets ?? []) {
      out[f.dimension] = f.terms.map((t: { term: string }) => t.term);
    }
    return out;
  }, [facetData]);

  const refreshQueue = useCallback(async () => {
    if (!accountId) return;
    try {
      setQueue(await listQueue(accountId));
    } catch (err) {
      console.warn('[capture] could not read the queue', err);
    }
  }, [accountId]);

  // ── Camera lifecycle ──────────────────────────────────────────────────────

  const openCamera = useCallback(async (id?: string) => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraError(null);

    try {
      const stream = await startStream(id ? { deviceId: id } : { facingMode: 'environment' });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => {});
        if (video.readyState < 1) {
          await new Promise((r) => video.addEventListener('loadedmetadata', r, { once: true }));
        }
      }
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      setResolution(`${settings.width}×${settings.height}`);
      setDeviceId(settings.deviceId || id || '');
      focusRef.current = detectFocusCapabilities(track);
    } catch (err) {
      setCameraError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const found = await listCameras();
        if (cancelled) return;
        setCameras(found);
        // A copy stand is usually the odd one out — prefer a document camera
        // when one is present, otherwise take the first.
        const preferred = found.find((c) => /ipevo|document|usb/i.test(c.label)) ?? found[0];
        await openCamera(preferred?.deviceId);
      } catch (err) {
        if (!cancelled) setCameraError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [openCamera]);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  // Reconnecting is the moment the queue exists for. Drain on the browser's
  // online event rather than polling.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // ── Capture ───────────────────────────────────────────────────────────────

  const takeShot = useCallback(
    async (side: Side) => {
      const video = videoRef.current;
      if (!video) return;
      try {
        if (afBeforeCapture && focusRef.current.singleShot) {
          setFocusing(true);
          await triggerAutofocus(focusRef.current, 600);
          setFocusing(false);
        }
        const frame = await captureFrame(video, colorCal);
        setShots((prev) => {
          const old = prev[side];
          if (old) URL.revokeObjectURL(old.url);
          return {
            ...prev,
            [side]: {
              blob: frame.blob,
              url: URL.createObjectURL(frame.blob),
              width: frame.width,
              height: frame.height,
            },
          };
        });
      } catch (err) {
        setFocusing(false);
        toast.error(err instanceof Error ? err.message : 'Capture failed');
      }
    },
    [afBeforeCapture, colorCal]
  );

  // F / B / O, the shortcuts the studio app trained its operator on. Skipped
  // while a field has focus, or typing a player's name starts taking photos.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && el.closest('input, textarea, select, [contenteditable]')) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 'f') void takeShot('front');
      else if (key === 'b') void takeShot('back');
      else if (key === 'o') void takeShot('other');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [takeShot]);

  const dropShot = (side: Side) => {
    setShots((prev) => {
      const old = prev[side];
      if (old) URL.revokeObjectURL(old.url);
      const next = { ...prev };
      delete next[side];
      return next;
    });
  };

  const clearItem = () => {
    Object.values(shots).forEach((s) => s && URL.revokeObjectURL(s.url));
    setShots({});
    setFields({});
    setTaxonomies({});
    setOcrText('');
    setOcrSuggestions({ names: [], numbers: [] });
  };

  // ── Crop + OCR ────────────────────────────────────────────────────────────

  const cropShot = async (side: Side) => {
    const shot = shots[side];
    if (!shot) return;
    setBusy(`Loading the cropper…`);
    try {
      // OpenCV is what makes the crop automatic; without it the same modal
      // still works as a manual rectangle, which is worth saying out loud
      // rather than silently degrading.
      const cvReady = await ensureOpenCv();
      if (!cvReady) toast.warning('OpenCV unavailable — manual crop only');
      const { openCropper } = await import('../../lib/capture/crop.js');
      setBusy(null);
      const cropped: Blob | null = await openCropper({
        blob: shot.blob,
        title: `Crop ${side}`,
      });
      if (!cropped) return;

      const bitmap = await createImageBitmap(cropped);
      URL.revokeObjectURL(shot.url);
      setShots((prev) => ({
        ...prev,
        [side]: {
          blob: cropped,
          url: URL.createObjectURL(cropped),
          width: bitmap.width,
          height: bitmap.height,
          cropped: true,
        },
      }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cropping failed');
    } finally {
      setBusy(null);
    }
  };

  const readBack = async () => {
    const back = shots.back;
    if (!back) return;
    setBusy('Loading OCR…');
    try {
      const ready = await ensureTesseract();
      if (!ready) throw new Error('Could not load Tesseract.js — check your connection');
      const { analyzeCardBack } = await import('../../lib/capture/ocr.js');
      const analysis = await analyzeCardBack(back.blob, (msg: string) => setBusy(msg));
      setOcrText(analysis.fullText || '');
      setOcrSuggestions({
        names: (analysis.names ?? []).slice(0, 4).map((n: { text: string }) => n.text),
        numbers: (analysis.numbers ?? []).slice(0, 4).map((n: { text: string }) => n.text),
      });
      toast.success('Read the card back');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'OCR failed');
    } finally {
      setBusy(null);
    }
  };

  // ── Calibration ───────────────────────────────────────────────────────────

  const calibrateBackground = async () => {
    const video = videoRef.current;
    if (!video) return;
    setBusy('Sampling the background…');
    if (focusRef.current.singleShot) await triggerAutofocus(focusRef.current, 500);
    const profile = buildBackgroundProfile(video);
    setBusy(null);
    if (!profile) {
      toast.error('That background is too gray to key on — use a more saturated mat');
      return;
    }
    setBgCal(profile);
    toast.success(`Background calibrated · peak hue ${profile.peakH}°`);
  };

  const openColorPicker = () => {
    const video = videoRef.current;
    if (!video) return;
    const still = grabStill(video);
    if (!still) {
      toast.error('Camera is not ready yet');
      return;
    }
    setColorPicker(still);
  };

  const pickGray = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) * (canvas.width / rect.width));
    const y = Math.round((e.clientY - rect.top) * (canvas.height / rect.height));
    const sampled = sampleNeutralGray(canvas, x, y);
    if (!sampled) return;
    setColorCal(sampled.calibration);
    setColorPicker(null);
    if (!sampled.looksNeutral) {
      toast.warning('That patch does not look neutral — the correction may overshoot');
    } else {
      toast.success(`Colour calibrated · sample ${sampled.calibration.swatch}`);
    }
  };

  // Draw the frozen frame into the picker canvas once it is mounted.
  const pickerCanvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      if (!node || !colorPicker) return;
      node.width = colorPicker.width;
      node.height = colorPicker.height;
      node.getContext('2d')?.drawImage(colorPicker, 0, 0);
    },
    [colorPicker]
  );

  // ── Save + sync ───────────────────────────────────────────────────────────

  const sync = useCallback(async () => {
    if (!accountId) return;
    setSyncing(true);
    try {
      const result = await drainQueue(client, accountId);
      await refreshQueue();
      if (result.uploaded) {
        setSavedCount((n) => n + result.uploaded);
        toast.success(`Saved ${result.uploaded} item${result.uploaded === 1 ? '' : 's'}`);
      }
      if (result.failed) {
        toast.error(result.errors[0] ?? `${result.failed} item(s) still queued`);
      }
    } finally {
      setSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, client, refreshQueue]);

  const saveItem = async () => {
    if (!accountId) return;
    const captured = SIDES.filter((s) => shots[s]);
    if (!captured.length) return;

    const cleanTaxonomies: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(taxonomies)) {
      if (value.trim()) cleanTaxonomies[key] = [value.trim()];
    }
    const cleanFields: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value.trim()) cleanFields[key] = value.trim();
    }

    const item = await enqueue({
      sku: mintSku(),
      accountId,
      images: captured.map((side) => ({
        role: side,
        blob: shots[side]!.blob,
        width: shots[side]!.width,
        height: shots[side]!.height,
      })),
      fields: cleanFields,
      taxonomies: cleanTaxonomies,
      title: cleanFields.player_name || undefined,
      ocrText: ocrText || undefined,
      publishStatus: 'draft',
      captureMeta: buildCaptureMeta(bgCal, colorCal),
    });

    // The item is durable now, so the shooting position is free immediately —
    // the upload happens behind the next card rather than in front of it.
    clearItem();
    await refreshQueue();

    if (!navigator.onLine) {
      toast.message('Queued — it will upload when you reconnect');
      return;
    }

    try {
      await uploadItem(client, item);
      setSavedCount((n) => n + 1);
      toast.success('Saved to the collection');
    } catch (err) {
      toast.error(
        `${err instanceof Error ? err.message : 'Upload failed'} — kept in the queue for retry`
      );
    } finally {
      await refreshQueue();
    }
  };

  const discardQueued = async (id: string) => {
    await removeItem(id);
    await refreshQueue();
  };

  const capturedSides = SIDES.filter((s) => shots[s]);
  const pending = queue.filter((q) => q.status !== 'done');

  if (accountsLoading) return <Loader label="Loading" className="p-6 text-cmp-ink-3" />;

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h1 className="text-2xl font-semibold text-cmp-ink flex items-center gap-2">
                      <Camera className="w-6 h-6" /> Capture
                    </h1>
                    <p className="text-sm text-cmp-ink-2 mt-1">
                      Photograph front and back, crop, read the card back, save. Items are stored on
                      this device first, so a dropped connection costs a retry, not the session.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!online && (
                      <Badge variant="outline" className="text-[11px] gap-1">
                        <CloudOff className="w-3 h-3" /> Offline
                      </Badge>
                    )}
                    {savedCount > 0 && (
                      <button
                        onClick={() => navigate('/collection')}
                        className="text-xs text-cmp-ink-2 underline hover:text-cmp-ink"
                      >
                        {savedCount} saved this session →
                      </button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void sync()}
                      disabled={syncing || !pending.length}
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-2">
                        {pending.length ? `Sync ${pending.length} queued` : 'Nothing queued'}
                      </span>
                    </Button>
                  </div>
                </header>

                {busy && (
                  <Card className="p-3 text-sm bg-cmp-raise border border-cmp-line text-cmp-ink flex items-center gap-2">
                    <Loader size="inline" label={busy} />
                  </Card>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6 items-start">
                  {/* ── Left: live camera ───────────────────────────────── */}
                  <div className="space-y-4 min-w-0">
                    <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-3">
                      <div className="flex items-end gap-3 flex-wrap">
                        <label className="text-xs text-cmp-ink-2 space-y-1">
                          <span className="block">Camera</span>
                          <select
                            value={deviceId}
                            onChange={(e) => void openCamera(e.target.value)}
                            className="bg-cmp-surface border border-cmp-line rounded px-2 py-1.5 text-sm text-cmp-ink min-w-[14rem]"
                          >
                            {cameras.length === 0 && <option value="">No camera found</option>}
                            {cameras.map((c, i) => (
                              <option key={c.deviceId} value={c.deviceId}>
                                {c.label || `Camera ${i + 1}`}
                              </option>
                            ))}
                          </select>
                        </label>
                        <div className="text-xs text-cmp-ink-2">
                          <span className="block">Resolution</span>
                          <span className="text-sm text-cmp-ink tabular-nums">{resolution}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            setFocusing(true);
                            await triggerAutofocus(focusRef.current, 600);
                            setFocusing(false);
                          }}
                          disabled={!focusRef.current.singleShot && !focusRef.current.continuous}
                        >
                          <Focus className="w-4 h-4" />
                          <span className="ml-2">{focusing ? 'Focusing…' : 'Refocus'}</span>
                        </Button>
                        <label className="flex items-center gap-2 text-xs text-cmp-ink-2">
                          <input
                            type="checkbox"
                            checked={afBeforeCapture}
                            onChange={(e) => setAfBeforeCapture(e.target.checked)}
                          />
                          AF before capture
                        </label>
                      </div>

                      {cameraError && (
                        <p className="text-sm text-red-400">
                          Could not open the camera: {cameraError}
                        </p>
                      )}

                      <div className="relative bg-black rounded overflow-hidden aspect-video flex items-center justify-center">
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-contain"
                        />
                        {focusing && (
                          <span className="absolute top-2 left-2 text-[11px] px-2 py-1 rounded bg-black/60 text-white">
                            Focusing…
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {SIDES.map((side) => (
                          <Button
                            key={side}
                            onClick={() => void takeShot(side)}
                            disabled={!!cameraError}
                            className="capitalize"
                          >
                            {side}
                            <kbd className="ml-2 text-[10px] opacity-70 uppercase">{side[0]}</kbd>
                          </Button>
                        ))}
                        <span className="text-xs text-cmp-ink-3">
                          Re-shooting a side replaces it.
                        </span>
                      </div>
                    </Card>

                    <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-3">
                      <div className="text-sm font-medium">Calibration</div>
                      <p className="text-xs text-cmp-ink-3">
                        Session-only, and stamped onto every photo taken under it. Re-tare after
                        changing mat, lighting, or camera position. Background is metadata that
                        drives auto-crop; colour is applied to the pixels before saving.
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void calibrateBackground()}
                        >
                          Calibrate background
                        </Button>
                        <Button variant="outline" size="sm" onClick={openColorPicker}>
                          Calibrate colour
                        </Button>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap text-xs">
                        {bgCal && (
                          <span className="flex items-center gap-2 text-cmp-ink-2">
                            <span
                              className="inline-block w-4 h-4 rounded border border-cmp-line"
                              style={{ background: bgCal.swatch }}
                            />
                            hue {bgCal.peakH}° · S≥{bgCal.sMin} · V≥{bgCal.vMin}
                            <button
                              onClick={() => setBgCal(null)}
                              className="underline hover:text-cmp-ink"
                            >
                              clear
                            </button>
                          </span>
                        )}
                        {colorCal && (
                          <span className="flex items-center gap-2 text-cmp-ink-2">
                            <span
                              className="inline-block w-4 h-4 rounded border border-cmp-line"
                              style={{ background: colorCal.swatch }}
                            />
                            gains R×{colorCal.gains.r.toFixed(2)} B×{colorCal.gains.b.toFixed(2)}
                            <button
                              onClick={() => setColorCal(null)}
                              className="underline hover:text-cmp-ink"
                            >
                              clear
                            </button>
                          </span>
                        )}
                        {!bgCal && !colorCal && (
                          <span className="text-cmp-ink-3">Not calibrated.</span>
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* ── Right: the item being assembled ─────────────────── */}
                  <div className="space-y-4 min-w-0">
                    <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">This item</div>
                        {capturedSides.length > 0 && (
                          <button
                            onClick={clearItem}
                            className="text-xs text-cmp-ink-3 underline hover:text-cmp-ink"
                          >
                            discard
                          </button>
                        )}
                      </div>

                      {capturedSides.length === 0 ? (
                        <p className="text-sm text-cmp-ink-3">
                          Nothing captured yet. Press <kbd>F</kbd> for the front.
                        </p>
                      ) : (
                        <div className="grid grid-cols-3 gap-3">
                          {capturedSides.map((side) => {
                            const shot = shots[side]!;
                            return (
                              <figure key={side} className="space-y-1">
                                <div className="aspect-[3/4] bg-cmp-surface rounded overflow-hidden">
                                  <img
                                    src={shot.url}
                                    alt={side}
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <figcaption className="text-[11px] text-cmp-ink-2 capitalize flex items-center gap-1">
                                  {side}
                                  {shot.cropped && <Check className="w-3 h-3" />}
                                </figcaption>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => void cropShot(side)}
                                    title="Crop and rectify"
                                    className="text-[11px] text-cmp-ink-3 hover:text-cmp-ink inline-flex items-center gap-1"
                                  >
                                    <CropIcon className="w-3 h-3" /> crop
                                  </button>
                                  <button
                                    onClick={() => dropShot(side)}
                                    title="Remove this photo"
                                    className="text-[11px] text-cmp-ink-3 hover:text-cmp-ink inline-flex items-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </figure>
                            );
                          })}
                        </div>
                      )}

                      {shots.back && (
                        <div className="space-y-2">
                          <Button variant="outline" size="sm" onClick={() => void readBack()}>
                            <ScanText className="w-4 h-4" />
                            <span className="ml-2">Read the card back</span>
                          </Button>
                          {(ocrSuggestions.names.length > 0 ||
                            ocrSuggestions.numbers.length > 0) && (
                            <div className="space-y-1 text-xs">
                              {ocrSuggestions.names.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-cmp-ink-3">player:</span>
                                  {ocrSuggestions.names.map((n) => (
                                    <button
                                      key={n}
                                      onClick={() => setFields((f) => ({ ...f, player_name: n }))}
                                      className="px-2 py-0.5 rounded bg-cmp-bronze-soft text-cmp-ink"
                                    >
                                      {n}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {ocrSuggestions.numbers.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-cmp-ink-3">card #:</span>
                                  {ocrSuggestions.numbers.map((n) => (
                                    <button
                                      key={n}
                                      onClick={() => setFields((f) => ({ ...f, card_number: n }))}
                                      className="px-2 py-0.5 rounded bg-cmp-bronze-soft text-cmp-ink"
                                    >
                                      {n}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        {FIELD_ORDER.map((f) => (
                          <label key={f.key} className="text-xs text-cmp-ink-2 space-y-1">
                            <span className="block">{f.label}</span>
                            <Input
                              value={fields[f.key] ?? ''}
                              onChange={(e) =>
                                setFields((prev) => ({ ...prev, [f.key]: e.target.value }))
                              }
                            />
                          </label>
                        ))}
                        {TAXONOMY_ORDER.map((t) => (
                          <label key={t.key} className="text-xs text-cmp-ink-2 space-y-1">
                            <span className="block">{t.label}</span>
                            <Input
                              list={`facet-${t.key}`}
                              value={taxonomies[t.key] ?? ''}
                              onChange={(e) =>
                                setTaxonomies((prev) => ({ ...prev, [t.key]: e.target.value }))
                              }
                            />
                            <datalist id={`facet-${t.key}`}>
                              {(facetTerms[t.key] ?? []).map((term) => (
                                <option key={term} value={term} />
                              ))}
                            </datalist>
                          </label>
                        ))}
                      </div>
                      <p className="text-[11px] text-cmp-ink-3">
                        Sport, league and manufacturer are closed vocabularies — a term that is not
                        already in your schema will be rejected on save. Everything here is
                        optional; items save as drafts.
                      </p>

                      <Button
                        onClick={() => void saveItem()}
                        disabled={capturedSides.length === 0}
                        className="w-full"
                      >
                        <UploadCloud className="w-4 h-4" />
                        <span className="ml-2">Save and start the next</span>
                      </Button>
                    </Card>

                    {pending.length > 0 && (
                      <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink space-y-2">
                        <div className="text-sm font-medium">
                          Queued on this device ({pending.length})
                        </div>
                        <ul className="space-y-2 max-h-64 overflow-y-auto">
                          {pending.map((q) => (
                            <li
                              key={q.id}
                              className="flex items-start justify-between gap-2 text-xs border-b border-cmp-line pb-2 last:border-0"
                            >
                              <div className="min-w-0">
                                <div className="text-cmp-ink truncate">
                                  {q.title || q.sku}{' '}
                                  <span className="text-cmp-ink-3">
                                    · {q.images.length} photo
                                    {q.images.length === 1 ? '' : 's'}
                                  </span>
                                </div>
                                {q.lastError && (
                                  <div className="text-red-400 break-words">{q.lastError}</div>
                                )}
                              </div>
                              <button
                                onClick={() => void discardQueued(q.id)}
                                title="Discard this queued item"
                                className="text-cmp-ink-3 hover:text-cmp-ink shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-cmp-ink-3">
                          Retrying is safe — each item keeps the SKU it was queued under, so a
                          replay updates the same card rather than adding a second one.
                        </p>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Colour-chart picker: a frozen frame the user clicks a gray patch on. */}
      {colorPicker && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setColorPicker(null);
          }}
        >
          <div className="bg-cmp-raise border border-cmp-line rounded max-w-[min(1100px,95vw)] max-h-[92vh] overflow-auto p-4 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <strong className="text-sm text-cmp-ink">
                Click the neutral gray patch on your colour chart
              </strong>
              <button
                onClick={() => setColorPicker(null)}
                className="text-xs text-cmp-ink-3 underline hover:text-cmp-ink"
              >
                cancel
              </button>
            </div>
            <canvas
              ref={pickerCanvasRef}
              onClick={pickGray}
              className="max-w-full h-auto cursor-crosshair"
            />
            <p className="text-xs text-cmp-ink-3">
              Pick the largest medium-gray patch — not white, not black.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
